'use client';
import { useCallback, useMemo } from 'react';
import { usePriceAndFitmentContext } from 'app/[lang]/[esite]/contexts/price-and-fitment-context/price-and-fitment-context';
import { CartResponse } from 'services/cart/types';
import { useGaEventContext } from 'app/[lang]/[esite]/(core)/(home)/_components/customer-account/gaevent-context-provider';
import { useBreakpoint } from '@cat-ecom/pcc-components';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';

export type ProductCardsGAEventsContextProps = {
  products: Product[];
  cartDetails: CartResponse;
  listNameId: string;
  activeTab: string;
};

export const useProductCardsGAEvent = ({
  products,
  cartDetails,
  listNameId,
  activeTab
}: ProductCardsGAEventsContextProps) => {
  const { priceAndAvailability } = usePriceAndFitmentContext();
  const isLoading = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const { gaEventCaptured } = useGaEventContext();
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();

  // combines price details with product details
  const getProductWithPriceDetails = useCallback(() => {
    const combinedProductPrice = products.map((product) => {
      const priceDetail = priceAndAvailability?.['items']?.find(
        (item) => item.partNumber === product.partNumber
      );
      return {
        ...product,
        productPriceData: {
          ...priceDetail,
          price: priceDetail?.price?.unformatted
        }
      };
    });
    return combinedProductPrice;
  }, [products, priceAndAvailability]);

  const viewCartPayload = useMemo(() => {
    const viewItemList = {
      item_list_id: listNameId,
      item_list_name: listNameId
    };

    return {
      products: getProductWithPriceDetails(),
      itemListData: viewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true,
      page: 1
    };
  }, [getProductWithPriceDetails, cartDetails, listNameId]);

  const isGAEventEnabled =
    isLoading !== undefined &&
    products?.length > 0 &&
    priceAndAvailability &&
    gaEventCaptured[activeTab];

  const handleClickGaEvent = () => {
    fireViewItemListEvent(viewCartPayload);
  };

  return {
    isGAEventEnabled,
    handleClickGaEvent
  };
};

export default useProductCardsGAEvent;
